import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { SectionHead } from "@/components/landing/HeroSections";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CheckCircle2 } from "lucide-react";

const BLANK = { contact_name: "", laundry_name: "", email: "", phone: "", town: "", message: "", website: "" };
const FIELDS = [["contact_name", "Your name", true], ["laundry_name", "Laundry name", true], ["email", "Email", true, "email"], ["phone", "Phone"], ["town", "Town"]];

export default function PartnerEnquiry({ d }) {
  const e = d.enquiry || {};
  const [f, setF] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (ev) => {
    ev.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/public/partner-enquiry", f);
      setDone(true);
      setF(BLANK);
      setTimeout(() => document.getElementById("talk")?.scrollIntoView({ behavior: "smooth" }), 50);
    }
    catch (err) { setError(formatApiError(err.response?.data?.detail) || "Could not send your enquiry"); }
    finally { setBusy(false); }
  };

  return (
    <section id="talk" className="scroll-mt-24 bg-slate-50" data-testid="partners-enquiry">
      <div className="mx-auto grid max-w-7xl gap-10 px-5 py-20 lg:grid-cols-12 lg:px-8 lg:py-24">
        <div className="lg:col-span-5"><SectionHead overline={e.overline} heading={e.heading} sub={e.subheading} /></div>
        <div className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-8 lg:col-span-7">
          {done ? (
            <div className="py-10 text-center" data-testid="enquiry-success">
              <CheckCircle2 className="mx-auto h-12 w-12 text-green-500" />
              <h3 className="mt-4 font-heading text-2xl font-extrabold text-slate-900">{e.success_title}</h3>
              <p className="mt-2 text-slate-500">{e.success_body}</p>
              <button onClick={() => setDone(false)} className="mt-6 text-sm font-bold text-brand hover:underline" data-testid="enquiry-again">Send another enquiry</button>
            </div>
          ) : (
            <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2" data-testid="enquiry-form">
              {FIELDS.map(([k, label, req, type]) => (
                <div key={k} className={`space-y-1.5 ${k === "email" ? "sm:col-span-2" : ""}`}>
                  <Label htmlFor={`enq-${k}`} className="font-semibold text-slate-700">{label}{req ? " *" : ""}</Label>
                  <Input id={`enq-${k}`} type={type || "text"} required={!!req} value={f[k]} onChange={(ev) => setF({ ...f, [k]: ev.target.value })} className="h-12 rounded-2xl bg-slate-50" data-testid={`enquiry-${k}`} />
                </div>
              ))}
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="enq-message" className="font-semibold text-slate-700">Message</Label>
                <Textarea id="enq-message" rows={4} value={f.message} onChange={(ev) => setF({ ...f, message: ev.target.value })} className="rounded-2xl bg-slate-50" data-testid="enquiry-message" />
              </div>
              <input type="text" name="website" tabIndex={-1} autoComplete="off" value={f.website} onChange={(ev) => setF({ ...f, website: ev.target.value })} className="absolute -left-[9999px] h-0 w-0 opacity-0" aria-hidden="true" data-testid="enquiry-hp" />
              {error && <p className="rounded-2xl bg-destructive/10 px-4 py-2.5 text-sm text-destructive sm:col-span-2" data-testid="enquiry-error">{error}</p>}
              <button type="submit" disabled={busy} className="h-12 rounded-full bg-brand text-base font-bold text-white shadow-lg shadow-brand/25 transition-[background-color,transform] hover:-translate-y-0.5 hover:bg-brand-hover disabled:opacity-60 sm:col-span-2" data-testid="enquiry-submit">
                {busy ? "Sending…" : e.button || "Send enquiry"}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
